/**
 * snake_case를 camelCase로 변환
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * camelCase를 snake_case로 변환
 */
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function getCaseAliases(key: string): string[] {
  const aliases = new Set<string>()
  const camelKey = snakeToCamel(key)
  const snakeKey = camelToSnake(key)

  if (camelKey !== key) {
    aliases.add(camelKey)
  }
  if (snakeKey !== key) {
    aliases.add(snakeKey)
  }

  return [...aliases]
}

/**
 * 객체의 속성값을 가져올 때 camelCase와 snake_case 모두 확인
 * 외부 PODO API가 camelCase로 응답하므로 두 형식 모두 처리
 */
export function getProperty<T>(obj: Record<string, unknown>, camelKey: string): T | undefined {
  const snakeKey = camelToSnake(camelKey)
  return (obj[camelKey] ?? obj[snakeKey]) as T | undefined
}

/**
 * snake_case 또는 camelCase 객체를 camelCase 객체로 변환
 * 외부 PODO API가 camelCase로 응답하므로 두 형식 모두 처리
 */
export function toCamelCase<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item)) as unknown as T
  }

  if (typeof obj !== 'object') {
    return obj as T
  }

  const source = obj as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(source)) {
    const camelKey = snakeToCamel(key)
    const value = source[key]

    // 이미 camelCase 키가 있으면 우선 사용
    if (result[camelKey] === undefined) {
      result[camelKey] = typeof value === 'object' ? toCamelCase(value) : value
    }
  }

  return result as T
}

/**
 * 원본 키를 유지하면서 camelCase와 snake_case alias를 모두 추가
 */
export function toCaseCompatible<T = unknown>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => toCaseCompatible(item)) as unknown as T
  }

  if (typeof obj !== 'object') {
    return obj as T
  }

  const source = obj as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(source)) {
    result[key] = toCaseCompatible(source[key])
  }

  for (const key of Object.keys(source)) {
    for (const alias of getCaseAliases(key)) {
      if (!(alias in result)) {
        result[alias] = result[key]
      }
    }
  }

  return result as T
}
